// One-line "what it does and why it matters" for every dashboard section (sidebar tooltips + Features map).
export const ADMIN_FEATURES = {
  home: "Your starting point: what's coming up, what needs attention and shortcuts to daily tasks.",
  analytics: "Charts of bookings, revenue and growth so you can see how the ecosystem is doing.",
  system_health: "Checks that payments, email and other connections are working so problems are caught early.",
  operations_view: "Daily, weekly and monthly totals for bookings and sales across every center.",
  appointments: "Every appointment across all centers, with filters to find, change or follow up on any booking.",
  scheduling: "Set up shifts and staffing so every center is covered at the right times.",
  services: "Create and edit the services people can book, with prices and durations.",
  products: "Manage items for sale in the shop and at the front desk.",
  packages: "Bundles of sessions sold at one price, so clients can pre-pay and save.",
  events: "Create and publish gatherings, workshops and live events with tickets.",
  rooms: "The rooms and spaces at each center that can be booked.",
  locations: "Add and edit centers, their hours, and review partner center applications.",
  venue: "Approve or decline requests to host something at a venue.",
  center_roster: "See which enhancers work at which center.",
  users: "Find any member, change their role or access, and see their account.",
  clients: "Everything a client has bought and booked, in one place.",
  vendors: "Review applications from people who want to join as an enhancer or vendor.",
  profile_fields: "Choose the extra questions members answer on their profile.",
  memberships: "Set up membership plans, prices and what each one includes.",
  groups: "Community groups members can join to connect around a topic.",
  moderation: "Review reported posts and comments to keep the community safe.",
  data_requests: "Handle member requests to download or delete their personal data.",
  revenue_split: "Set how each payment is shared between the enhancer, the center and the ecosystem.",
  coupons: "Discount codes for promotions, and how often they've been used.",
  reconciliation: "Match payments received against bookings and sales so the books balance.",
  gift_cards: "Issue, look up and void gift cards and see their balances.",
  commissions: "Commission rates and earnings for people who sell on the ecosystem's behalf.",
  payouts: "Pay enhancers and centers what they've earned, and see what's on hold.",
  orders: "Shop orders that need packing or shipping, and their status.",
  payroll: "Staff hours and pay for each pay period.",
  agreements: "The agreements people sign (waivers, contracts) and who has signed them.",
  corporate: "Requests from companies interested in wellness programs for their teams.",
  feedback: "Answers from feedback surveys sent after sessions and events.",
  forms_all: "Every custom form and the responses it has collected.",
  suggestions: "Ideas members have sent in for improving the ecosystem.",
  testimonials_mod: "Approve member reviews and manage the testimonials shown on the site.",
  "google-reviews": "Pull in Google reviews to show on the site.",
  crm: "Send email campaigns to groups of members.",
  email_templates: "Edit the wording of the automatic emails and messages people receive.",
  digest: "Preview and send the regular summary email that goes out to members.",
  showcase: "Approve what members share in the Community Showcase.",
  broadcasting: "Go live, publish podcasts and videos, and edit, unpublish or delete any video.",
  articles: "Write and publish articles for the site.",
  pages: "Build and edit site pages without code.",
  homepage: "Change the words and images on the home page.",
  menu: "Choose what appears in the site's main menu.",
  help: "Help articles members and enhancers can read when they're stuck.",
  seo: "Page titles, descriptions and search settings so people find the site on Google.",
  gauge: "The GAUGE wellness pillars members use to track their path.",
  "gauge-copy": "The wording shown throughout the GAUGE experience.",
  "gauge-suggestions": "Practices GAUGE suggests to members based on their answers.",
  "gauge-matches": "How members are matched to enhancers and offerings from their GAUGE results.",
  stewardship_ops: "Volunteer and giving-back opportunities, plus the impact goals they count toward.",
  import: "Bring in members, services or other data from a spreadsheet.",
  integrations: "Connect outside services like payments, email, calendars and video.",
  location_hub: "A single view of every center's details and performance.",
  cleanup: "Find and tidy test or outdated records safely.",
  duplicates: "Spot and merge records that were entered twice.",
};

export const ENHANCER_FEATURES = {
  "Get Started": "Your starting point: what's coming up, what needs attention and your setup checklist.",
  Analytics: "How your bookings, earnings and profile views are trending.",
  Profile: "The public page members see: photo, bio, specialties and credentials.",
  Articles: "Write articles that appear on your profile and in the ecosystem.",
  Social: "Link your social accounts and share your work.",
  "Media & Broadcasting": "Go live, publish podcasts and videos, and edit, unpublish or delete your videos.",
  Availability: "The days and hours members can book you.",
  Bookings: "Your upcoming and past appointments.",
  Clients: "The people who have booked with you and when you last saw them.",
  "Intake Form": "Questions new clients answer before their first session with you.",
  Forms: "Custom forms you can send to clients and collect answers from.",
  "Event Roster": "Who is registered for each of your events.",
  "My Shifts": "Shifts you're scheduled to work at a center.",
  Services: "The sessions you offer, with prices and durations.",
  Events: "Workshops and gatherings you host.",
  Products: "Items you sell through the shop.",
  Packages: "Bundles of your sessions sold at one price.",
  Courses: "Online courses with lessons people can take at their own pace.",
  Stewardship: "Volunteer and giving-back opportunities you can join.",
  Locations: "Places you offer sessions, including your own space.",
  "Our Centers": "Apply to offer your services at one of our centers.",
  "Schedule at EYW": "Book time in an ecosystem room for your sessions.",
  "Centers & Schedule": "Your centers, their room calendars next to your schedule, and instant booking of open time.",
  Earnings: "What you've earned, what's been paid, and your Stripe payout setup.",
  Orders: "Shop orders for your products that need fulfilling.",
  Notifications: "Updates about bookings, payments and your account.",
  Inbox: "Messages from members and the ecosystem team.",
  Integrations: "Connect Zoom, Google Calendar and YouTube to your studio.",
};

export const CENTER_FEATURES = {
  Operations: "Your starting point: today's appointments, what needs attention and your setup checklist.",
  "Day Close": "Daily totals for closing out the register at the end of the day.",
  Calendar: "Everything booked at your center on one calendar.",
  Appointments: "Your center's booking and cancellation rules, plus a shortcut to the front desk.",
  Blackouts: "Days or times your center is closed so nobody can book them.",
  Approvals: "Appointment requests waiting for you to accept or decline.",
  Services: "The services offered at your center, and requests from enhancers to add new ones.",
  Events: "Events held at your center.",
  Packages: "Bundles of sessions sold at your center.",
  Rooms: "Rooms and spaces at your center that can be booked.",
  Bookings: "Every booking at your center, with payment status.",
  "Media & Broadcasting": "Go live, publish videos for your center, and edit, unpublish or delete them.",
  Enhancers: "Enhancers working at your center and their applications.",
  Roster: "Who is scheduled to provide services and when.",
  Members: "Clients and members who visit your center.",
  "Intake Form": "Questions new clients answer before their first visit.",
  Forms: "Custom forms you can send and collect answers from.",
  Coupons: "Discount codes for your center.",
  Reconciliation: "Match payments received against bookings so the books balance.",
  "Cash Drawer": "Track cash in and out of the register.",
  Payouts: "What your center has earned and your Stripe payout setup.",
  Rentals: "Room rentals and what renters owe.",
  Payroll: "Staff hours and pay for each pay period.",
  Orders: "Shop orders that need packing or pickup.",
  Notifications: "Updates about your account.",
  Inbox: "New bookings, changes and alerts for your center.",
  Inquiries: "Questions from visitors interested in your center, with replies sent by email.",
  Location: "Your center's public page: description, photos, address and opening hours.",
};
